
import Vue from 'vue';
import Component from 'vue-class-component';
import MissionMap from '@/components/Mission/MissionMap.vue';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { SimplePath } from '@/flightpath-components/models/SimplePath';
import { getModule } from 'vuex-module-decorators';
import MissionStore from '@/store/modules/missions';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { SimplePoint } from '@/flightpath-components/models/SimplePoint';
import { GPSLocation } from '@/flightpath-components/models/GPSLocation';
import { Guid } from 'guid-typescript';
import { ImplementOperationsModel } from '@/flightpath-components/models/implement/ImplementOperationsModel';
import { Parameters, ImplementKeys, SprayerProductTypes } from '@/services/Parameters';
import MissionToolTypes from '@/services/MissionToolTypes';
import { SortHelper } from '@/services/SortHelper';
import DurationFinder from '@/utils/DurationFinder';
import GlobalStore from '@/store/modules/global';
import { IntersectActionHelper } from '@/utils/IntersectActionHelper';
import { Watch } from 'vue-property-decorator';

export enum MissionPageMode {
  Fields = 'Fields',
  Paths = 'Paths',
  Parameters = 'Parameters'
}

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'AddNewMission', inject: ['container'], components: { MissionMap } })
export default class AddNewMission extends Vue {
  globalStore = getModule(GlobalStore, this.$store);
  loading = false;
  panel = [0];
  missionStore = getModule(MissionStore, this.$store);
  saveDialog = false;
  cancelDialog = false;
  warnDialog = false;
  warning = '';
  newMissionName = '';
  pageMode: MissionPageMode = MissionPageMode.Fields;
  paths: SimplePath[] = [];
  fields: FieldOperationsModel[] = [];
  implements: ImplementOperationsModel[] = [];
  steps = 0;
  vineWidth = 0;
  volume = 0;
  sprayProduct = '';
  parameters: Map<string, string> = new Map();
  parametersValid = false;
  speed = 0;
  engineSpeed = 0;
  missionTypes = MissionToolTypes.missionTypes;
  selectedMissionType: { text: string; value: string } | null = null;
  toolTypes = MissionToolTypes.toolTypes;
  selectedToolType: { text: string; value: string } | null = null;
  implementKeys = ImplementKeys;
  selectedFieldIndex = 0;
  windowSize = {
    x: 0,
    y: 0
  };

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  @Watch('selectedMissionType')
  async onSelectedMissionType() {
    await this.$nextTick();
    this.validate();
  }

  get sprayerProducts(): SprayerProductTypes[] {
    return Object.values(SprayerProductTypes);
  }

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  async mounted() {
    this.onResize();
    this.changeToFieldsMode();
    await this.getImplements();
  }

  validate(): boolean {
    return (this.$refs as any).form.validate();
  }

  get listTitle() {
    return this.$t(this.pageMode.valueOf().toLowerCase());
  }

  get detailTitle() {
    switch (this.pageMode) {
      case MissionPageMode.Fields:
        return this.missionStore.newMissionField != null ? this.missionStore.newMissionField.FieldName : '';
      case MissionPageMode.Paths:
        return this.missionStore.newMissionPath != null ? this.missionStore.newMissionPath.Name : '';
      case MissionPageMode.Parameters:
        return this.$t('views.missions.mission_summary');
      default:
        return '';
    }
  }

  get missionNameValid() {
    return this.newMissionName.length > 0;
  }

  get lgMapCols() {
    return this.pageMode != MissionPageMode.Parameters ? 7 : 9;
  }

  get mdMapCols() {
    return this.pageMode != MissionPageMode.Parameters ? 6 : 8;
  }

  checkPageMode(mode: string) {
    return this.pageMode.valueOf() == mode;
  }

  async changeToFieldsMode() {
    this.selectedFieldIndex = 0;
    this.steps = 1;
    this.pageMode = MissionPageMode.Fields;
    this.missionStore.setNewMissionField(null);
    this.missionStore.setFieldAreaM2(0);
    await this.getFields();
  }

  async changeToPathsMode() {
    if (this.missionStore.fieldAreaM2 == 0) {
      this.warning = this.$t('views.missions.dialog.select_field').toString();
      this.warnDialog = true;
      return;
    }
    this.steps = 2;
    this.pageMode = MissionPageMode.Paths;
    this.missionStore.setNewMissionPath(null);
    this.missionStore.setWorkAreaAreaM2(0);
    this.missionStore.setPathLengthKm(0);
    await this.getPaths();
  }

  changeToParametersMode() {
    if (this.missionStore.pathLengthKm == 0) {
      this.warning = this.$t('views.missions.dialog.select_path').toString();
      this.warnDialog = true;
      return;
    }
    this.steps = 3;
    this.pageMode = MissionPageMode.Parameters;
  }

  async getFields() {
    if (this.flightPathService == undefined) return;

    this.loading = true;
    await this.flightPathService
      .getAvailableFields(this.globalStore.accountId)
      .then((response) => {
        this.fields = FieldOperationsModel.toModelCollection(JSON.stringify(response.data));
        this.fields = this.fields.sort((a, b) => SortHelper.compareStrings(a.FieldName as string, b.FieldName as string));
        this.loading = false;
      })
      .catch(() => (this.loading = true));
  }

  async getPaths() {
    if (this.flightPathService == undefined) return;

    this.loading = true;
    await this.flightPathService
      .getPathsByCustomerId(this.globalStore.accountId)
      .then((response) => {
        const allPaths = SimplePath.toModelCollection(JSON.stringify(response.data));
        this.paths = allPaths.filter((x) => x.FieldID != null && x.FieldID != '' && x.FieldID != Guid.EMPTY.toString());
        this.paths = this.paths.sort((a, b) => SortHelper.compareStrings(a.Name as string, b.Name as string));
        this.loading = false;
      })
      .catch(() => (this.loading = true));
  }

  get pathsByField() {
    if (this.missionStore.newMissionField == null || this.paths == null) return [];
    return this.paths.filter((o) => o.FieldID == this.missionStore.newMissionField?.FieldID);
  }

  async getImplements() {
    if (this.flightPathService == undefined) return;

    const response = await this.flightPathService.getAllImplements(this.globalStore.accountId);
    this.implements = ImplementOperationsModel.toModelCollection(JSON.stringify(response.data));
  }

  async selectField(field: FieldOperationsModel) {
    const deselect = field != null && field == this.missionStore.newMissionField;
    this.missionStore.setNewMissionField(deselect ? null : field);
  }

  async selectPath(path: SimplePath) {
    if (!this.flightPathService) return;
    const response = await this.flightPathService.getPathByPathId(path.PathID, this.globalStore.accountId);
    const simplePath = SimplePath.toModel(JSON.stringify(response.data));
    const deselect = simplePath != null && simplePath == this.missionStore.newMissionPath;
    this.missionStore.setNewMissionPath(deselect ? null : simplePath);
  }

  async saveMission() {
    if (this.flightPathService == undefined) return;

    let newMission = new SinglePathMission();
    newMission.CustomerID = this.globalStore.accountId;
    newMission.MissionID = Guid.create().toString();
    newMission.MissionName = this.newMissionName;
    newMission.ReferencePathID = this.missionStore.newMissionPath?.PathID as string;
    newMission.FieldBoundary = {
      DrawPoints: this.missionStore.newMissionField?.BoundaryPoints.map((value) => ({ Latitude: value.Item1, Longitude: value.Item2 })) as GPSLocation[]
    };
    newMission.Path = this.missionStore.newMissionPath?.Path as SimplePoint[];
    newMission.WorkAreas = this.missionStore.newMissionPath?.WorkAreas as SimplePoint[][];
    newMission.CropAreas = this.missionStore.newMissionPath?.CropAreas as SimplePoint[][];
    newMission.ReferenceFieldID = this.missionStore.newMissionField?.FieldID as string;
    newMission.TargetSpeedKph = this.speed;
    newMission.ImplementCode = this.selectedImplement.ImplementID;

    this.saveParameters();
    newMission = newMission.setApplicationParameters(this.parameters);

    const missionWithIntersects = IntersectActionHelper.modifyMissionIntersects(newMission, true);

    await this.flightPathService.insertOrUpdateMission(missionWithIntersects);

    this.missionStore.setNewlyCreatedMission(missionWithIntersects);

    this.$router.push({ name: 'AddAndEditMissions' });
  }

  async nextStep() {
    if (this.pageMode == MissionPageMode.Fields) await this.changeToPathsMode();
    else if (this.pageMode == MissionPageMode.Paths) this.changeToParametersMode();
  }

  async previousStep() {
    if (this.pageMode == MissionPageMode.Paths) {
      this.missionStore.setNewMissionPath(null);
      this.missionStore.setWorkAreaAreaM2(0);
      this.missionStore.setPathLengthKm(0);
      await this.changeToFieldsMode();
    } else if (this.pageMode == MissionPageMode.Parameters) {
      await this.changeToPathsMode();
    }
  }

  cancelNewMission() {
    this.$router.push({ name: 'AddAndEditMissions' });
  }

  cancelSaveMission() {
    this.saveDialog = false;
    this.newMissionName = '';
  }

  validateInput() {
    this.validate();
    if (this.selectedImplement.ImplementID == Guid.EMPTY.toString() && this.selectedImplementKey != this.implementKeys.none) {
      this.warning = 'Please select a mission type';
      this.warnDialog = true;
    } else {
      this.saveDialog = true;
    }
  }

  saveParameters() {
    this.parameters.set(Parameters.implementType, this.selectedImplementKey);
    this.parameters.set(Parameters.engineSpeed, this.engineSpeed.toString());
    if (this.selectedImplementKey == ImplementKeys.sprayer) {
      this.parameters.set(Parameters.vineWidth, this.vineWidth.toString());
      this.parameters.set(Parameters.volume, this.volume.toString());
      this.parameters.set(Parameters.sprayProduct, this.sprayProduct.toString());
    }
  }

  get availableToolTypes() {
    // only get tool types (implementts) that are currently in the database
    return this.toolTypes.filter((x) => this.implements.findIndex((implement) => implement.ImplementName == x.value && x.value != 'weeder') > -1);
  }

  get selectedImplementKey(): string {
    if (this.selectedMissionType == null) {
      return '';
    } else if (this.selectedMissionType.value != ImplementKeys.sprayer) {
      return this.selectedToolType?.value ?? '';
    }
    return ImplementKeys.sprayer;
  }

  get selectedImplement(): ImplementOperationsModel {
    return this.implements.find((x) => x.ImplementName == this.selectedImplementKey) ?? new ImplementOperationsModel();
  }

  get estimatedDurationHours() {
    if (this.speed == 0 || this.missionStore.newMissionPath == null) return '0 h 0 min';
    return DurationFinder.findEstimatedMissionDurationHoursMinutes(
      this.missionStore.newMissionPath.Path.map((x) => new window.google.maps.LatLng(x.Lat, x.Long)),
      this.speed
    );
  }
}
